import type { AppLogContext } from '#ui-shared/utils/logger/contexts/log-contexts';
import type { LoggerAdapter } from '#ui-shared/utils/logger/LoggerClass';

export interface ConsoleLoggerAdapterConfig {
  /** Whether to enable the console log -- should be set with env. variables (prod vs dev) */
  isEnabled: boolean;
}

/**
 * This is a simple adapter for logging to the browser console. It implements the LoggerAdapter interface and provides methods for logging messages at different levels (log, error, warn, info, debug).
 */
export class ConsoleLoggerAdapter implements LoggerAdapter {
  constructor(readonly config: ConsoleLoggerAdapterConfig) {}

  log(message: string, context?: AppLogContext): void {
    if (this.config.isEnabled) {
      if (context) {
        console.debug(`[LOG] ${message}`, context);
      }
      else {
        console.debug(`[LOG] ${message}`);
      }
    }
  }

  error(message: string, context?: AppLogContext): void {
    if (this.config.isEnabled) {
      if (context) {
        console.error(`[ERROR] ${message}`, context);
      }
      else {
        console.error(`[ERROR] ${message}`);
      }
    }
  }

  warn(message: string, context?: AppLogContext): void {
    if (this.config.isEnabled) {
      if (context) {
        console.warn(`[WARN] ${message}`, context);
      }
      else {
        console.warn(`[WARN] ${message}`);
      }
    }
  }

  info(message: string, context?: AppLogContext): void {
    if (this.config.isEnabled) {
      if (context) {
        console.debug(`[INFO] ${message}`, context);
      }
      else {
        console.debug(`[INFO] ${message}`);
      }
    }
  }

  debug(message: string, context?: AppLogContext): void {
    if (this.config.isEnabled) {
      if (context) {
        console.debug(`[DEBUG] ${message}`, context);
      }
      else {
        console.debug(`[DEBUG] ${message}`);
      }
    }
  }

  /**
   * Determines whether console logging is enabled based on the environment variables. If the variable is not set or cannot be parsed, it defaults to true, enabling console logging by default.
   */
  static isConsoleLogEnabled(): boolean {
    const env = import.meta.env.VITE_FRONTEND_CONSOLE_LOG_ENABLED;

    if (!env) {
      console.warn('Enabling console log by default as VITE_FRONTEND_CONSOLE_LOG_ENABLED is not set.');
      return true;
    }

    try {
      const parsedValue = JSON.parse(env);

      return typeof parsedValue === 'boolean' ? parsedValue : true;
    }
    catch (error) {
      console.warn('Error parsing VITE_FRONTEND_CONSOLE_LOG_ENABLED:', error);
      console.warn('Enabling console log by default due to parsing error.');
      return true;
    }
  }
}
